import { Article, ArrowBack, People } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  ListItemDecorator,
  Stack,
  TabList,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/joy";
import { Outlet, useMatchRoute } from "@tanstack/react-router";
import { ApiQuest, ApiQuestDetail } from "griptonite-api-client/types";
import { ReactNode, useMemo } from "react";

import { useQuestQuery, useUpdateQuestMutation } from "@/hooks/quests";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";
import formatPeriod from "@/utils/formatPeriod";

import { ChallengeStatusChip } from "../ChallengeCard";
import { IconButtonLink, TabLink } from "../Links";
import LoyaltyNavToggle from "../Loyalty/LoyaltyNavToggle";
import getPublishWarnings from "./getPublishWarnings";

const TABS = [
  { value: "profile", label: "Profile", icon: <Article /> },
  { value: "entries", label: "Entries", icon: <People /> },
] as const;

// The list entry that stands in while the detail loads can't show whether milestones are missing,
// so publishing waits for the detail.
const DraftBanner = ({
  quest,
  venueUuid,
}: {
  quest: ApiQuest | ApiQuestDetail;
  venueUuid?: string;
}) => {
  const publishMutation = useUpdateQuestMutation(quest.uuid, venueUuid);
  const publishWarnings = useMemo(() => getPublishWarnings(quest), [quest]);
  const isDetailLoaded = "pointsThresholds" in quest;

  const publishButton = (
    <Button
      size="lg"
      color="primary"
      disabled={!isDetailLoaded || publishWarnings.allWarnings.length > 0}
      loading={publishMutation.isPending}
      loadingPosition="start"
      onClick={() =>
        publishMutation.mutate(
          { isHidden: false },
          {
            onError: () => alert("Unknown error occurred. Please try again or contact support"),
          },
        )
      }
    >
      Publish
    </Button>
  );

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        border: "2px solid",
        borderColor: "neutral.400",
        backgroundColor: "white",
        maxWidth: 800,
        borderRadius: 8,
        padding: 1.5,
        marginBottom: 1.5,
      }}
    >
      <Box>
        <Chip size="lg" sx={{ fontWeight: "bold" }} variant="solid">
          Draft mode
        </Chip>
        <Typography color="neutral" level="body-sm">
          The quest is only visible to staff members
        </Typography>
      </Box>
      {publishWarnings.allWarnings.length ? (
        <Tooltip
          title="To publish the quest, finish going through the tabs and fix any issues"
          size="md"
          placement="bottom"
        >
          <Box>{publishButton}</Box>
        </Tooltip>
      ) : (
        publishButton
      )}
    </Stack>
  );
};

// Without a `questUuid`, a quest is being created: its form comes in as `children` and the Entries
// tab waits for it to exist. `background` sits behind the tab's content; the type picker uses a
// lighter one than the grey behind the quest's white cards.
export default function QuestOverview({
  questUuid,
  children,
  background = "neutral.200",
}: {
  questUuid?: string;
  children?: ReactNode;
  background?: string;
}) {
  const venueUuid = useCurrentVenueUuid();
  const { data: venue } = useCurrentVenueQuery();
  const { data: quest } = useQuestQuery(questUuid, venueUuid);
  const matchRoute = useMatchRoute();
  // Fuzzy, so the Entries tab stays selected while an entry's panel is open beneath it.
  const activeTab =
    questUuid &&
    matchRoute({ to: "/gym/$venueUuid/loyalty/quests/$questUuid/entries", fuzzy: true })
      ? "entries"
      : "profile";

  return (
    <Box sx={{ display: "flex", height: "100dvh", flexDirection: "column" }}>
      <Stack direction="row" alignItems="center" gap={1} sx={{ paddingX: 2, paddingY: 1 }}>
        <LoyaltyNavToggle />
        <IconButtonLink
          size="sm"
          variant="plain"
          color="neutral"
          aria-label="Back to quests"
          from="/gym/$venueUuid"
          to="/gym/$venueUuid/loyalty/quests"
        >
          <ArrowBack />
        </IconButtonLink>
        <Stack sx={{ minWidth: 0 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              level="body-lg"
              fontWeight="bold"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {quest ? quest.name : questUuid ? "Quest" : "New quest"}
            </Typography>
            {quest ? <ChallengeStatusChip status={quest.status} /> : null}
          </Stack>
          <Typography level="body-sm" color="neutral">
            {quest ? formatPeriod(quest, venue?.timezone || "UTC") : ""}
          </Typography>
        </Stack>
      </Stack>
      <Tabs value={activeTab}>
        <TabList>
          {TABS.map(({ value, label, icon }) => (
            <TabLink
              key={value}
              value={value}
              sx={{ flex: 1, height: 40 }}
              from="/gym/$venueUuid/loyalty/quests/$questUuid"
              to={questUuid ? `./${value}` : "/gym/$venueUuid/loyalty/quests/create"}
              search={questUuid ? undefined : true}
              disabled={!questUuid && value !== "profile"}
              replace
            >
              <ListItemDecorator>{icon}</ListItemDecorator>
              {label}
            </TabLink>
          ))}
        </TabList>
      </Tabs>
      <Stack
        sx={{
          flex: 1,
          minHeight: 0,
          backgroundColor: background,
          padding: 1.5,
        }}
      >
        {quest?.isHidden ? <DraftBanner quest={quest} venueUuid={venueUuid} /> : null}
        {/* Each tab lays out its own card, so it can put things beside it. */}
        {children ?? <Outlet />}
      </Stack>
    </Box>
  );
}
